/**
 * Raster pixel thuần TypeScript cho charforge (phase B).
 *
 * Mọi sprite 48×48 đều được vẽ bằng code lên Raster (bộ đệm RGBA), KHÔNG dùng
 * Canvas/DOM nên cùng một hàm vẽ chạy được ở cả hai nơi:
 * - tools/charforge/bake.ts (node): bake thành PNG atlas lúc build.
 * - src/assets/placeholder.ts (browser): vẽ fallback lúc chạy khi chưa bake.
 *
 * Mỗi sprite nhận một RNG đã seed theo ID (deterministic): cùng ID luôn cho
 * cùng một ảnh, bake bao nhiêu lần cũng giống nhau từng byte.
 */

/** Màu dạng '#rrggbb'; null = xóa pixel (trong suốt). */
export type Mau = string | null;

/** Hàm vẽ một sprite lên raster, kèm RNG đã seed theo ID sprite. */
export type VeSprite = (r: Raster, rng: () => number) => void;

/** Bộ đệm điểm ảnh RGBA. */
export class Raster {
  readonly rong: number;
  readonly cao: number;
  private readonly duLieu: Uint8ClampedArray;

  constructor(rong = 48, cao = 48) {
    this.rong = rong;
    this.cao = cao;
    this.duLieu = new Uint8ClampedArray(rong * cao * 4);
  }

  /** Tô toàn bộ nền (mặc định trong suốt nếu không gọi). */
  nen(mau: string): void {
    const [r, g, b] = giaiMau(mau);
    for (let i = 0; i < this.duLieu.length; i += 4) {
      this.duLieu[i] = r;
      this.duLieu[i + 1] = g;
      this.duLieu[i + 2] = b;
      this.duLieu[i + 3] = 255;
    }
  }

  /** Vẽ một điểm ảnh; tọa độ ngoài khung thì bỏ qua. Hỗ trợ alpha-blend. */
  px(x: number, y: number, mau: Mau): void {
    const xi = Math.round(x);
    const yi = Math.round(y);
    if (xi < 0 || yi < 0 || xi >= this.rong || yi >= this.cao) return;
    const i = (yi * this.rong + xi) * 4;
    if (mau === null) {
      this.duLieu[i + 3] = 0;
      return;
    }
    const [r, g, b, a] = giaiMau(mau);
    if (a === 255) {
      this.duLieu[i] = r;
      this.duLieu[i + 1] = g;
      this.duLieu[i + 2] = b;
      this.duLieu[i + 3] = 255;
      return;
    }
    // Alpha-blend lên pixel đích (toán tử "over").
    const sa = a / 255;
    const da = this.duLieu[i + 3]! / 255;
    const oa = sa + da * (1 - sa);
    if (oa === 0) {
      this.duLieu[i + 3] = 0;
      return;
    }
    this.duLieu[i] = Math.round((r * sa + this.duLieu[i]! * da * (1 - sa)) / oa);
    this.duLieu[i + 1] = Math.round((g * sa + this.duLieu[i + 1]! * da * (1 - sa)) / oa);
    this.duLieu[i + 2] = Math.round((b * sa + this.duLieu[i + 2]! * da * (1 - sa)) / oa);
    this.duLieu[i + 3] = Math.round(oa * 255);
  }

  /** Đọc màu tại một điểm (dạng '#rrggbb' hoặc null nếu trong suốt). */
  lay(x: number, y: number): Mau {
    const xi = Math.round(x);
    const yi = Math.round(y);
    if (xi < 0 || yi < 0 || xi >= this.rong || yi >= this.cao) return null;
    const i = (yi * this.rong + xi) * 4;
    if (this.duLieu[i + 3] === 0) return null;
    return mauSangHex(this.duLieu[i]!, this.duLieu[i + 1]!, this.duLieu[i + 2]!);
  }

  /** Bản sao RGBA để mã hóa PNG / đưa lên canvas. */
  sangRGBA(): Uint8ClampedArray<ArrayBuffer> {
    return new Uint8ClampedArray(this.duLieu);
  }

  /** Chép toàn bộ raster nguồn vào vị trí (x, y) của raster này. */
  chepTu(nguon: Raster, x: number, y: number): void {
    for (let sy = 0; sy < nguon.cao; sy++) {
      for (let sx = 0; sx < nguon.rong; sx++) {
        const mau = nguon.lay(sx, sy);
        if (mau !== null) this.px(x + sx, y + sy, mau);
      }
    }
  }
}

/** '#rrggbb' hoặc 'rgba(r,g,b,a)' → [r, g, b, alpha 0–255]. */
export function giaiMau(mau: string): [number, number, number, number] {
  const mHex = /^#([0-9a-fA-F]{6})$/.exec(mau);
  if (mHex?.[1]) {
    const hex = mHex[1];
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
      255,
    ];
  }
  const mRgba = /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([\d.]+)\s*\)$/.exec(
    mau,
  );
  if (mRgba) {
    const kenh = (v: string | undefined): number => Math.max(0, Math.min(255, Number(v)));
    return [kenh(mRgba[1]), kenh(mRgba[2]), kenh(mRgba[3]), Math.round(Number(mRgba[4]) * 255)];
  }
  throw new Error(`Màu không hợp lệ: ${mau} (cần dạng #rrggbb hoặc rgba(r,g,b,a)).`);
}

function mauSangHex(r: number, g: number, b: number): string {
  const h = (v: number): string => v.toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

/** Hình chữ nhật đặc. */
export function rect(r: Raster, x: number, y: number, w: number, h: number, mau: Mau): void {
  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      r.px(x + dx, y + dy, mau);
    }
  }
}

/** Khung viền hình chữ nhật, dày `day` pixel. */
export function vien(
  r: Raster,
  x: number,
  y: number,
  w: number,
  h: number,
  mau: Mau,
  day = 1,
): void {
  rect(r, x, y, w, day, mau);
  rect(r, x, y + h - day, w, day, mau);
  rect(r, x, y, day, h, mau);
  rect(r, x + w - day, y, day, h, mau);
}

/** Đoạn thẳng (Bresenham). */
export function duongThang(
  r: Raster,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  mau: Mau,
): void {
  let x = Math.round(x0);
  let y = Math.round(y0);
  const xd = Math.round(x1);
  const yd = Math.round(y1);
  const dx = Math.abs(xd - x);
  const dy = -Math.abs(yd - y);
  const sx = x < xd ? 1 : -1;
  const sy = y < yd ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    r.px(x, y, mau);
    if (x === xd && y === yd) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y += sy;
    }
  }
}

/** Hình tròn đặc. */
export function tron(r: Raster, cx: number, cy: number, bk: number, mau: Mau): void {
  for (let y = Math.floor(cy - bk); y <= Math.ceil(cy + bk); y++) {
    for (let x = Math.floor(cx - bk); x <= Math.ceil(cx + bk); x++) {
      if ((x - cx) * (x - cx) + (y - cy) * (y - cy) <= bk * bk) {
        r.px(x, y, mau);
      }
    }
  }
}

/** Viền tròn, dày `day` pixel. */
export function tronVien(r: Raster, cx: number, cy: number, bk: number, mau: Mau, day = 1): void {
  for (let y = Math.floor(cy - bk); y <= Math.ceil(cy + bk); y++) {
    for (let x = Math.floor(cx - bk); x <= Math.ceil(cx + bk); x++) {
      const d2 = (x - cx) * (x - cx) + (y - cy) * (y - cy);
      if (d2 <= bk * bk && d2 > (bk - day) * (bk - day)) {
        r.px(x, y, mau);
      }
    }
  }
}

/** Tô bóng đơn giản: một cột tối ở mép phải của vùng chữ nhật. */
export function bongPhai(r: Raster, x: number, y: number, w: number, h: number, mau: Mau): void {
  rect(r, x + w - 2, y, 2, h, mau);
}

/** Làm tối một màu hex theo hệ số (0–1). */
export function toiDi(mau: string, heSo = 0.75): string {
  const [r, g, b] = giaiMau(mau);
  return mauSangHex(Math.round(r * heSo), Math.round(g * heSo), Math.round(b * heSo));
}

/** Làm sáng một màu hex theo hệ số (0–1, 1 = trắng). */
export function sangLen(mau: string, heSo = 0.25): string {
  const [r, g, b] = giaiMau(mau);
  const tronLen = (v: number): number => Math.round(v + (255 - v) * heSo);
  return mauSangHex(tronLen(r), tronLen(g), tronLen(b));
}

/** Băm chuỗi thành số nguyên 32-bit (FNV-1a) — seed cho RNG. */
export function bamId(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** RNG deterministic mulberry32. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Bảng màu cozy dùng chung cho charforge: tone ấm, bão hòa vừa phải,
 * nhất quán giữa mọi sprite.
 */
export const BANG_MAU = {
  // Da.
  daSang: '#f2c89b',
  daTrung: '#e0a878',
  daToi: '#b97f52',
  // Tóc.
  tocDen: '#2e2620',
  tocNau: '#6b4a2a',
  tocVang: '#d9a83f',
  tocBac: '#cfcfcf',
  // Chung.
  trang: '#ffffff',
  den: '#222222',
  bong: 'rgba(0,0,0,0.18)',
} as const;
